// [claude-code 2026-04-18] Greeting chips now have explicit hover (gold tint + icon color lift)
//   and focus-visible ring for keyboard users. Chips already auto-dismiss the greeting by sending
//   a message (the thread store swaps to the messages list on first append), so no extra dismiss
//   handler needed — verified by reading FintheonThread.tsx:578 (greeting only renders when
//   messages.length === 0).
// [claude-code 2026-03-06] Extracted AnalysisGreeting from ChatInterface — greeting + suggestion chips
// [claude-code 2026-03-11] Chips now wired to skill system via onSkillSend
// [claude-code 2026-03-14] Fintheon rebrand: Dawn Dispatch/Weekly Tribune chips, Roman greetings, new agent titles
// [claude-code 2026-03-16] Shimmer greeting animation, removed border/bg/emoji from greeting area
import { useState, useEffect } from "react";
import { BarChart3, CalendarCheck, Brain, Eye } from "lucide-react";
import { useFintheonAgents } from "../../contexts/FintheonAgentContext";

const SUGGESTION_CHIPS: {
  label: string;
  skillId: string;
  prompt: string;
  icon: typeof BarChart3;
}[] = [
  {
    label: "Dawn Dispatch",
    skillId: "mdb_report",
    prompt: "Run the MDB report for today's session",
    icon: BarChart3,
  },
  {
    label: "The Weekly Tribune",
    skillId: "wt",
    prompt: "Give me The Weekly Tribune summary",
    icon: CalendarCheck,
  },
  {
    label: "Psych Eval",
    skillId: "psych_eval",
    prompt: "Run a full psych eval on my recent trading",
    icon: Brain,
  },
  {
    label: "Update my Blindspots",
    skillId: "blindspots",
    prompt: "Update and review my trading blindspots",
    icon: Eye,
  },
];

function getGreeting(traderName?: string): string {
  const hour = new Date().getHours();
  if (hour < 12) return `Ave, ${traderName || "Trader"}. The markets stir.`;
  if (hour <= 17) return "The Forum is active. What needs our attention?";
  return "The day's battles are done. What of the next conquest?";
}

interface ChatGreetingProps {
  onSend: (msg: string) => void;
  onSkillSend?: (skillId: string, msg: string) => void;
  isLoading: boolean;
}

export function ChatGreeting({
  onSend,
  onSkillSend,
  isLoading,
}: ChatGreetingProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  let activeAgent: {
    name: string;
    icon: string;
    sector: string;
    description: string;
  } | null = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const ctx = useFintheonAgents();
    activeAgent = ctx.activeAgent;
  } catch {
    // Provider not mounted yet — fallback
  }

  const agent = activeAgent || {
    name: "Harper",
    icon: "H",
    sector: "CAO",
    description: "Chief Analyst Officer — executive strategy and oversight",
  };
  const greeting = getGreeting();

  const handleChipClick = (chip: (typeof SUGGESTION_CHIPS)[number]) => {
    if (onSkillSend) {
      onSkillSend(chip.skillId, chip.prompt);
    } else {
      onSend(chip.prompt);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-5 max-w-[780px] mx-auto w-full">
      {/* Agent name — large, centered */}
      <div className="flex flex-col items-center gap-2.5">
        <h2 className="text-[22px] font-semibold text-[#f0ead6] tracking-tight">
          {agent.name}
        </h2>
      </div>

      {/* Large greeting — shimmer animation */}
      <h1
        className={[
          "text-[26px] font-bold tracking-tight text-center leading-snug mt-1",
          mounted ? "greeting-animate greeting-settle" : "opacity-0",
        ].join(" ")}
      >
        {greeting}
      </h1>

      {/* Suggestion chips — 2x2 grid */}
      <div className="grid grid-cols-2 gap-3 w-full mt-3">
        {SUGGESTION_CHIPS.map((chip, index) => {
          const Icon = chip.icon;
          return (
            <button
              key={index}
              onClick={() => handleChipClick(chip)}
              disabled={isLoading}
              className="flex items-center gap-3 px-4 py-3.5 bg-transparent border border-[rgba(199,159,74,0.10)] hover:border-[rgba(199,159,74,0.20)] hover:bg-[var(--fintheon-accent)]/5 disabled:opacity-50 rounded-lg text-left transition-all group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/60"
            >
              <Icon className="w-[18px] h-[18px] text-[#f0ead6]/30 transition-colors shrink-0 group-hover:text-[var(--fintheon-accent)]" />
              <span className="text-[13px] text-[#f0ead6]/72 group-hover:text-[#f0ead6] transition-colors">
                {chip.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
